import React from "react";
import ReactMarkdown from "react-markdown";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  LinearScale,
  PointElement,
  LineElement,
  CategoryScale,
} from "chart.js";

//import { Line, Scatter } from "react-chartjs-2";
//import { RangeSlider } from "flowbite-react";
import {Hero, PageContent} from "../../components";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import remarkMath from "remark-math";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  CategoryScale,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    title: {
      display: true,
      text: "Chart.js Bar Chart - Stacked",
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
    },
  },
};

export const data1 = {
  datasets: [
    {
      label: "A dataset",
      data: Array.from({ length: 100 }, () => ({
        x: Math.floor(Math.random() * 1000),
        y: Math.floor(Math.random() * 1000),
      })),
      backgroundColor: "rgba(255, 99, 132, 1)",
    },
    {
      label: "A dataset",
      data: Array.from({ length: 100 }, () => ({
        x: Math.floor(Math.random() * 1000),
        y: Math.floor(Math.random() * 1000),
      })),
      backgroundColor: "rgba(99, 255, 132, 1)",
    },
    {
      label: "A dataset",
      data: Array.from({ length: 100 }, () => ({
        x: Math.floor(Math.random() * 1000),
        y: Math.floor(Math.random() * 1000),
      })),
      backgroundColor: "#FFFF00",
    },
  ],
};

const labels = ["January", "February", "March", "April", "May", "June", "July"];

export const data2 = {
  labels,
  datasets: [
    {
      label: "Dataset 1",
      data: labels.map(() => Math.floor(Math.random() * 1000)),
      borderColor: "rgb(255, 99, 132)",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
    },
    {
      label: "Dataset 2",
      data: labels.map(() => Math.floor(Math.random() * 1000)),
      borderColor: "rgb(53, 162, 235)",
      backgroundColor: "rgba(53, 162, 235, 0.5)",
    },
  ],
};

function Model() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col -z-10">
      <div className="flex overflow-y-hidden relative w-screen h-auto">
        <Hero title={"Model"}>
          Mathematical models are the abstract canvases upon which {" "}
          <span className="text-lg font-black text-RougePastel md:text-xl lg:text-2xl">
            universe paints
          </span>{" "}
          its patterns.
        </Hero>
      </div>

      <PageContent>
        {`
# Objective and model
To have a prediction of the efficiency of our system we decided to create a realistic model that would help us to get theoretical data for our system. This model consists of 3 parts that work with each other. We have the first place which is a 2D graph that displays points. Each point is considered as an entity of our system. 
In the 2nd part we can see 2 graphs. The first one gives us the evolution of the population in the media. The second one presents us the changes of temperature in the bioreactor. 
We decided to simulate a fed batch bioreactor as it possesses interesting characteristics for our project. 
- constant mass of heat to be taken by the heat pump.
- maximum yields
- programmed removal of waste 
- longest log phase, equivalent to less maintenance to add microorganism

The model is on GitLab ([available here](https://gitlab.igem.org/2023/software-tools/ionis-paris/-/tree/main/model?ref_type=heads)).
         

<div
  className="flex justify-center items-center h-auto flex-auto md:max-w-[1500px] gap-[30px]"
  id="page-content"
>
  <iframe
    title="Ionis-Paris: Model_Representation of the photobioreactor (2023) [English]"
    className="video"
    src="https://video.igem.org/videos/embed/f00f834e-a219-43f6-8274-5c7ca9c98bc5"
    frameBorder="0"
    sandbox="allow-same-origin allow-scripts allow-popups"
  ></iframe>
</div>
<br><br><br>

# The model
## The bioreator:
In our bioreactor, microorganisms could get stuck on the walls: this is why we designed an infinite box, a commonly used representation in gas models. This approach effectively relocates particles, as illustrated in the figure below. Additionally, this method offers the advantage of enhancing the scalability of the model. In principle, the outcomes observed within a smaller box can be extrapolated to forecast the behavior of a larger-scale box. 

<div class="image_frame">
   <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/model-figure-1.jpg">
    <p><b>Figure 1: Periodic boundary conditions.</b> A particle crosses an edge of the box and travels back to the opposite edge.</p>
</div>


## The Entity:
To construct the model, we used 3 entities:
- **EE2** : At the beginning of the model, the equivalent of 78.4 ng/L of EE2 is created to simulate the initial concentration of EE2 found in the wastewater [1].
- ***C. reinhardtii*** : Our microalgae is spawned at the beginning of the model. Each is able to multiply itself after 8 hours as it is the known replication time for *C.reinhardtii*. The multiplication is limited to a maximum of 1.2 x 10<sup>7</sup> cells /mL as we know is the maximum concentration of *C.reinhardtii* in coculture with *S.meliloti* [2].
- ***S.meliloti*** : Our bacteria is spawned at the beginning of the model. Each is able to multiply itself after 3 hours as it is the known replication time for *S.meliloti*. The multiplication is limited to a maximum of 1x10<sup>8</sup> cells/mL as we know that his is the maximum concentration of *C.reinhardtii* in the coculture [2].

## Temperature:
One of the principal parameters  is the temperature produced by the microalgae. Thanks to the litteral, we were able to estimate that the theoretical heat production would be of 3.5°C per hour if *C.reinhardtii* is concentrated at **1.2x10<sup>7</sup> cell/mL.** [3,4,5].
 
Access to the [Theoretical estimation of Heat production by AOX transformed C.reinhardtii](https://static.igem.wiki/teams/4577/wiki/project/model/theoretical-estimation-of-heat-production-by-aox-transformed-c-reinhardtii.pdf)

By taking numbers from the theoretical estimation we are able to estimate the heat production according to the number of cells in our model of known volume (10nL). 
We know that we need 4.8 x 10<sup>-6</sup> J to rise 1 µL of water of 1 °C and that a single microalgae produce 2.3  x  10<sup>-8</sup> J/H. This give us the following equation where Y is the heat production (°C/H) and X the number of microalgae: 

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/formule-1.jpg">
</div>

## Heat pump:
Now that we know how much heat our system will produce, we need to add the heat pump. The heat pump will transport and amplify the heat produced in the photobioreactor and deliver it to dwellings.
In our model the heat pump is not shown. But there are three different levels of activation depending on the amount of heat you want to withdraw from the photobioreactor. 

## Heat-related mortality:
Our goal is to produce as much heat as possible. But we need to remember that there is a limit!  Indeed if the heat is too high, there is a risk of killing the microalgae. To counter this, we decided to do a coculture of *C.reinhardtii* with *S.meliloti* as this co culture is known to raise *C.reinhardtii* heat resistance [2]. By contacting  Dr Halverson and Pr Xie for unpublished data,  we have learnt that the heat starts to affect *C.rheinardtii* in co culture at 37°C : after 5 days at 42° , 6.99 x 10<sup>6</sup> cells of *C.reinhardtii* die[2].
The number of deaths cells of C.reinhardtii per day when the temperature goes above 37°C can be represented by the following formula, V<sub>death</sub> being the speed of death per day and T, the temperature in the bioreactor in °C :

V<sub>death</sub> = (T-37)  x 1,165,000

S.meliloti is not concerned by the heat rise, since their number does not fall after 5 days at 42°C [2].

## EE2 concentration:
The second objective of our system would be to eliminate EE2 from the wastewater. Knowing that the EE2 concentration in wastewater is 78.4 ng/L [1]. This would give us 1,595,016 molecules of EE2 in the 10 nL of wastewater to simulate. This number being too high, one dot stands for 5,000 numbers of molecules giving us a starting point at 319 points. 
According to the 2021 HiZJU iGEM team, the bacteria modified with *hao-amoA* genes are able to degrade EE2 by 50% in 48  hours. By considering that the bacteria is at maximal concentration and efficient, and that our *S.meliloti* will have the same degradation rate than the bacteria *E. Coli* BL21 from the HiZJU team, we can find the following equation, with Y the  number of EE2 to eliminate on this round.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/formule-3.jpg">
</div>

## The fed-batch like system: 
As said earlier the model would be a fed batch model, bringing a non-continuous flow of influent and effluent wastewater. 
The flow has a huge impact on how the bioreactor behaves: the outflow subtract part of the component from the media like the EE2 for exemple. This substraction does not apply to the microalgae and the bacteria as they are kept inside the bioreactor by a filter. 
And the inflow : 
-	brings new unit of EE2
-	lowers the temperature of the batch as the wastewater is colder than the wastewater that is heated in the bioreactor. 

In our model, the flow rate is represented both in terms of outflow from the bioreactor and inflow of wastewater, specifying the percentages involved in each case. 
To simulate the outflow we simply subtract the entity in function of the percentage of flow rate chosen: 
With X the number of microalgae to be subtracted: 

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/nouveau-x.jpg">
</div>

To simulate the inflow, we need to add new EE2 and to change the temperature of the wastewater. As the inflow brings fresh wastewater with an untreated concentration of EE2 and a temperature of 12°C ,it will impact those two parameters inside of the bioreactor.318 was the number of points representing the EE2 quantity in the whole bioreactor before treatment. And the flow rate representing what percentage of the bioreactor will be replaced with wastewater. We can calculate the number of EE2 points to add :    

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/ee2-points.jpg">
</div>

Concerning the heat change, as we replace a certain percentage of heated wastewater with fresh wastewater, we need to do a weighted average. This average is calculated with the temperature of the wastewater in the bioreactor and the temperature of fresh wastewater. But as their volume is different, we weight the average with the percentage of fresh wastewater added (flow rate) and the percentage of heated wastewater (100 - flow rate). This give us the following equation:

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/model/nouveau-heat-change.jpg">
</div>


# Conclusion:
This model gives us an approach on how the system will work depending on the inputs that we chose. This is a great tool that is necessary before each implementation of a project like ours, as it will help to know the capacities of this system at a low cost and time. Although our simulation is just a representation and not an exact reflection of the real world, we ensured its accuracy by using scientific sources for our data and calculations, to make it the most realistic as possible.
Now that we have a proper model, we can think of several ways to improve it. We can add new constraints about other parameters to be more precise, such as the air bubbling that will aerate and induce movement in the bioreactor. We can also think of doing experiments to develop the numbers that we obtained with the scientific paper and adapt it precisely for our purposes. 


## Sources: 
[1] Tang, Z., Liu, Z., Wang, H., Dang, Z., & Liu, Y. (2021). Occurrence and removal of 17α-ethynylestradiol (EE2) in municipal wastewater treatment plants: Current status and challenges. *Chemosphere*, 271, 129551. doi:10.1016/j.chemosphere.2021.129551

[2] Xie, B., Bishop, S., Stessman, D., Wright, D., Spalding, M. H., & Halverson, L. J. (2013). Chlamydomonas reinhardtii thermal tolerance enhancement mediated by a mutualistic interaction with vitamin B12-producing bacteria. *The ISME Journal*, 7(8), 1544–1555. doi:10.1038/ismej.2013.43

[3] Onda, Y., Kato, Y., Abe, Y., Ito, T., Ito-Inaba, Y., Morohashi, M., … Ito, K. (2007). Pyruvate-sensitive
AOX exists as a non-covalently associated dimer in the homeothermic spadix of the skunk cabbage,
symplocarpus renifolius. FEBS Letters, 581(30), 5852–5858. doi:10.1016/j.febslet.2007.11.061

[4] Macherel, D., Haraux, F., Guillou, H., & Bourgeois, O. (2021). The conundrum of hot mitochondria.
Biochimica et Biophysica Acta (BBA) - Bioenergetics, 1862(2), 148348.
doi:10.1016/j.bbabio.2020.148348

[5] Wiśniewski, J. R., Hein, M. Y., Cox, J., & Mann, M. (2014). A “proteomic ruler” for protein copy
number and concentration estimation without spike-in standards. Molecular &amp; Cellular
Proteomics, 13(12), 3497–3506. doi:10.1074/mcp.m113.037309 


          `}
          </PageContent>
        </div>
  );
}

export default Model;
